import type { RxInput } from '~/api/graphql/generated/types'
import type { Order, Prescription } from '~/types'
import type { Variation } from '~/types/entities/inventories'
import {
  convertOrderToRxInput,
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
  isValidClientTask,
} from '~/utils/prescription'

// Extended Order type for search results that include prescription-specific fields
type SearchResultOrder = Order & {
  administrationQuantityUnitId?: string
  categoryId?: string
  drugInfo?: Prescription['drugInfo']
  drugMasterName?: string
  globalInventoryId?: string
  globalInventoryMappingId?: string
  globalVariationId?: string
  globalVariationMappingId?: string
  prescriptionType?: Prescription['prescriptionType']
  rxType?: Prescription['rxType']
}

export const transferCompoundedValue = (
  variation: Variation | undefined,
  drugInfo: Prescription['drugInfo'],
): Variation | undefined => {
  if (!variation) return undefined

  const compoundedValue = variation.compounded ?? drugInfo?.compounded
  if (compoundedValue !== undefined && variation.compounded === undefined) {
    return { ...variation, compounded: compoundedValue } as Variation
  }

  return variation
}

export const sanitizePrescriptionForSwap = (
  prescription: Prescription,
  options: {
    preserveVariationForCompounding?: boolean
  } = {},
): Prescription => {
  const {
    id,
    createdAt,
    updatedAt,
    deleted,
    rxNo,
    prescribedDate,
    inventory,
    inventoryId,
    variationId,
    partNumber, // Exclude partNumber - Chewy items are weight/species-specific, must reselect for new patient
    variation,
    ...rest
  } = prescription

  const { preserveVariationForCompounding = false } = options

  let variationToUse = variation
  let globalVariationMappingToUse = prescription.globalVariationMapping
  let globalVariationToUse = prescription.globalVariation

  // Fallback to global fields if variation doesn't exist (for backward compatibility)
  if (!variationToUse) {
    variationToUse =
      prescription.globalVariationMapping || prescription.globalVariation
  }

  if (preserveVariationForCompounding) {
    const preservedVariation =
      variation ||
      prescription.globalVariationMapping ||
      prescription.globalVariation

    variationToUse = transferCompoundedValue(
      preservedVariation,
      prescription.drugInfo,
    )

    globalVariationMappingToUse = transferCompoundedValue(
      prescription.globalVariationMapping,
      prescription.drugInfo,
    )

    globalVariationToUse = transferCompoundedValue(
      prescription.globalVariation,
      prescription.drugInfo,
    )
  }

  return {
    ...rest,
    ...(variationToUse ? { variation: variationToUse } : {}),
    ...(preserveVariationForCompounding
      ? {
          variation: variationToUse,
          drugInfo: prescription.drugInfo,
          globalVariation: globalVariationToUse,
          globalVariationMapping: globalVariationMappingToUse,
        }
      : {}),
  } as Prescription
}

/**
 * Converts a search result Order to a Prescription for swap flow
 * Starts with sanitized base prescription and overrides with search result data
 */
export const convertSearchOrderToPrescription = (
  searchOrder: Order,
  basePrescription: Prescription,
): Prescription => {
  // Start with sanitized base prescription to preserve all common fields
  // (quantity, frequency, doctor, refills, etc.)
  const sanitizedBase = sanitizePrescriptionForSwap(basePrescription)

  // Cast to extended type that includes prescription-specific fields from search results
  const order = searchOrder as SearchResultOrder

  return {
    ...sanitizedBase,

    // Override with product details from search result
    name: order.name,
    categoryId: order.categoryId,
    drugMasterName: order.drugMasterName,
    drugInfo: order.drugInfo,

    // Override with global inventory/variation IDs for pricing lookup
    // Extract IDs from objects if direct ID fields are not available
    globalInventoryId: order.globalInventoryId || order.globalInventory?.id,
    globalInventoryMapping: order.globalInventoryMapping,
    globalInventoryMappingId:
      order.globalInventoryMappingId || order.globalInventoryMapping?.id,
    globalVariationId: order.globalVariationId || order.globalVariation?.id,
    globalVariationMapping: order.globalVariationMapping,
    globalVariationMappingId:
      order.globalVariationMappingId || order.globalVariationMapping?.id,
    // Also copy the objects themselves for workflow detection
    globalInventory: order.globalInventory,
    globalVariation: order.globalVariation,

    // Override with administration unit from search result if available
    administrationQuantityUnitId:
      order.administrationQuantityUnitId ||
      sanitizedBase.administrationQuantityUnitId,

    // Override rxType if available in search result
    rxType: order.rxType || sanitizedBase.rxType,

    // Set prescription type to CHEWY
    prescriptionType: order.prescriptionType || 'CHEWY',

    // Clear product-specific fields - user must enter new values for new product
    partNumber: undefined,
    totalQuantity: undefined,
    totalQuantityUnitId: undefined,
    totalQuantityCustomUnit: undefined,
    quantityDrugExpirationDate: undefined,
    compoundingReasonId: undefined,
    compoundingReason: undefined,
  } as Prescription
}

export const convertPrescriptionToRxInputForSwap = (
  prescription: Prescription,
  isFoodCatalogEnabled: boolean,
  currentUserId: string | null,
): RxInput => {
  const autoshipUnitId = getPrescriptionAutoshipUnitId(prescription)
  const autoshipFrequency = getPrescriptionAutoshipFrequency(prescription)

  return {
    ...convertOrderToRxInput(
      isValidClientTask(prescription.clientTasks)
        ? prescription
        : {
            ...prescription,
            clientTasks: null,
          },
      isFoodCatalogEnabled,
      autoshipUnitId,
      autoshipFrequency,
    ),
    signerId: currentUserId,
  }
}
